import { useEffect, useState } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import PageNav from "../../../components/common/PageNav";
import setTitle from "../../../components/common/SetTitle";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import { OutletIProps } from "../../../components/sitebase/Base.types";
import NotificationsListSearch from "../notifications/notifications-list/NotificationsListSearch";
import { InlineNotification, InlinePaginatedResponse } from "./Inline.types";
import { INLINE_MENU_LINKS } from "./InlineMenu";
import InlineNotificationRow from "./InlineNotificationRow";

import '../notifications/css/notificationslist.css';
import type { SearchActionParams } from '../notifications/Notification.types';

/**
 * The paginated list of INLINE (EDERA) notifications, laid out like the EFAS
 * notifications list. Both open and closed notifications are listed, live and
 * historical ones alike - each row links to its detail page.
 */
export default function InlineNotificationList() {
    const { setExtraMenuLinks } = useOutletContext<OutletIProps>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState<number>(parseInt(searchParams.get("page") || "1"));
    const [date, setDate] = useState<string>(searchParams.get("creation_month") || "");
    const [search, setSearch] = useState<string>(searchParams.get("search") || "");
    const [notifications, setNotifications] = useState<InlineNotification[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageNav, setPageNav] = useState<React.JSX.Element>();

    const searchAction = (params: SearchActionParams) => {
        setSearch(params.search || "");
        setDate(params.creation_month || "");
        setPage(1);
        setSearchParams(params as Record<string, string>);
    }

    useEffect(() => {
        setTitle("INLINE Notifications");
        setExtraMenuLinks(INLINE_MENU_LINKS, "Notifications");
    }, [setExtraMenuLinks]);

    useEffect(() => {
        setLoading(true);
        // the search bar hands back the month as `creation_month`, which the API takes as `issue_month`
        const searchArg = search ? `&search=${search}` : '';
        const monthArg = date ? `&issue_month=${date}` : '';
        fetch(`/api/inline/notifications/?format=json&active=all&historical=all&page=${page}${searchArg}${monthArg}`, { credentials: "include" })
            .then(async (response) => {
                const data = await response.json().catch(() => null);
                if (!response.ok) {
                    const message = data?.message || data?.detail || JSON.stringify(data) || "Unknown error";
                    throw new Error(`HTTP ${response.status}: ${message}`);
                }
                return data as InlinePaginatedResponse<InlineNotification>;
            })
            .then((data) => {
                setNotifications(data?.results || []);
                setPageNav(<PageNav page={page} maxPage={data.total_pages} onChange={setPage} totalCount={data.count} />);
            })
            .catch((error) => {
                console.error("Failed to load INLINE notifications:", error);
                toast.error(`Failed to load notifications: ${error instanceof Error ? error.message : String(error)}`);
            })
            .finally(() => setLoading(false));
    }, [page, search, date]);

    // the /inline routes render in the landing page layout, which has no side padding of
    // its own, so the page adds its own to line up with the rest of the site
    return (
        <Container fluid={true} className="px-3 px-md-4 px-xl-5 py-3">
            <ToastContainer position="top-center" />
            <Row className="mb-3">
                <Col><span className="efas-notifications-list-span">INLINE Notifications List</span></Col>
                <NotificationsListSearch search={search} date={date} onClick={searchAction} searchText="Source ID, Group, or Rule" />
            </Row>
            {loading ? <div className="mx-auto text-center py-5"><LoadingSpinner />Loading Notifications</div> :
                notifications.length === 0 ? <div className="py-5 text-center">No notifications found.</div> :
                    <>
                        <Table className="efas-notifications-table table-hover border">
                            <tbody data-testid="inline-notifications-list">
                                {notifications.map((notification) =>
                                    <InlineNotificationRow key={notification.id}
                                        notification={notification}
                                        return_details={{ page: page, search: search, date: date }} />)}
                            </tbody>
                        </Table>
                        {pageNav}
                    </>
            }
        </Container>
    );
}
